import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircleQuestion, ChevronDown, ChevronUp, Send, Clock, Eye, Sparkles } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DoubtThread from "@/components/doubt/DoubtThread";

const fmtTs = (secs) => {
  if (!secs && secs !== 0) return "";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const parseTsString = (str) => {
  if (!str) return 0;
  const cleaned = String(str).trim();
  if (!cleaned) return 0;
  
  if (cleaned.includes(":")) {
    const parts = cleaned.split(":").map(p => parseInt(p, 10) || 0);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    return parts[0] || 0;
  }
  
  const num = parseInt(cleaned, 10);
  return isNaN(num) ? 0 : num;
};

const STATUS_COLORS = {
  pending:   "bg-[#FFCC00]/10 text-[#FFCC00]",
  in_review: "bg-[#00F0FF]/10 text-[#00F0FF]",
  answered:  "bg-[#00FF88]/10 text-[#00FF88]",
  closed:    "bg-gray-100 dark:bg-white/5 text-[#52525B]",
};

export default function DoubtWidget({ lesson, iframeRef, videoRef }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [doubts, setDoubts] = useState([]);
  const [loadingDoubts, setLoadingDoubts] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tsInputVal, setTsInputVal] = useState("");
  const [selectedDoubtId, setSelectedDoubtId] = useState(null);
  const hasLoaded = useRef(false);
  const tsInputRef = useRef(null);

  const fetchMyDoubts = useCallback(async () => {
    if (!lesson?.lesson_id) return;
    setLoadingDoubts(true);
    try {
      const res = await axios.get(`${API}/doubts/my`, { withCredentials: true });
      const all = Array.isArray(res.data) ? res.data : [];
      setDoubts(all.filter((d) => d.lesson_id === lesson.lesson_id));
    } catch {
      // non-critical
    } finally {
      setLoadingDoubts(false);
    }
  }, [lesson?.lesson_id]);

  const handleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next && !hasLoaded.current) {
        hasLoaded.current = true;
        fetchMyDoubts();
      }
      return next;
    });
  };

  // Auto-detect current playback timestamp from player
  const captureTimestamp = () => {
    // 1. Direct HTML5 video element
    if (videoRef?.current && typeof videoRef.current.currentTime === "number") {
      const sec = Math.floor(videoRef.current.currentTime);
      setTsInputVal(fmtTs(sec));
      toast.success(`Captured video timestamp: ${fmtTs(sec)}`);
      return;
    }

    // 2. YouTube iframe postMessage
    let resolved = false;
    if (iframeRef?.current?.contentWindow) {
      try {
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: "listening", id: 1 }),
          "*"
        );
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "getCurrentTime", args: "" }),
          "*"
        );
      } catch {}
    }

    const listener = (e) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (data?.event === "infoDelivery" && data?.info?.currentTime != null) {
          const sec = Math.floor(data.info.currentTime);
          setTsInputVal(fmtTs(sec));
          resolved = true;
          window.removeEventListener("message", listener);
          toast.success(`Captured video timestamp: ${fmtTs(sec)}`);
        }
      } catch {}
    };

    window.addEventListener("message", listener);

    setTimeout(() => {
      window.removeEventListener("message", listener);
      if (!resolved) {
        // Friendly guidance for external players (Google Drive, Vimeo, etc.)
        if (tsInputRef.current) {
          tsInputRef.current.focus();
        }
        toast.info("Type the time shown in the video player above (e.g. 08:32)", {
          duration: 3500
        });
      }
    }, 800);
  };

  const adjustTs = (offset) => {
    const current = parseTsString(tsInputVal) || 0;
    const next = Math.max(0, current + offset);
    setTsInputVal(fmtTs(next));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    const finalTs = parseTsString(tsInputVal);
    try {
      await axios.post(`${API}/doubts`, {
        course_id: lesson.course_id,
        course_name: lesson.course_name || null,
        lesson_id: lesson.lesson_id,
        lesson_name: lesson.title,
        doubt_text: text,
        timestamp_in_video: finalTs,
      }, { withCredentials: true });
      toast.success("Doubt submitted! Your mentor will respond soon.");
      setText("");
      setTsInputVal("");
      fetchMyDoubts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit doubt");
    } finally {
      setSubmitting(false);
    }
  };

  // If viewing a specific doubt thread
  if (selectedDoubtId) {
    return (
      <div className="terminal-card rounded-2xl p-5 border border-slate-200 dark:border-white/10 shadow-xs">
        <DoubtThread
          doubtId={selectedDoubtId}
          onBack={() => { setSelectedDoubtId(null); fetchMyDoubts(); }}
        />
      </div>
    );
  }

  return (
    <div className="terminal-card rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-xs">
      {/* Toggle header */}
      <button
        onClick={handleOpen}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <MessageCircleQuestion className="w-4 h-4 text-[#A855F7]" />
          </div>
          <div className="text-left">
            <p className="text-slate-900 dark:text-white font-outfit font-semibold text-sm">Doubt Centre</p>
            <p className="text-[10px] text-slate-500 dark:text-[#52525B] font-jetbrains">
              {doubts.length > 0 ? `${doubts.length} doubt${doubts.length > 1 ? "s" : ""} asked for this lesson` : "Ask a mentor a question about this lesson"}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-200 dark:border-white/[0.06] p-4 space-y-4 animate-in slide-in-from-top-2 duration-150">
          {/* Submit form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            
            {/* ── Direct Video Timestamp Bar ─────────────────────── */}
            <div className="flex items-center justify-between flex-wrap gap-2.5 p-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02]">
              <div className="flex items-center gap-2 flex-wrap">
                <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-xs font-jetbrains font-semibold text-slate-700 dark:text-zinc-300">
                  Video Timestamp:
                </span>
                
                {/* Time Input */}
                <div className="relative flex items-center">
                  <input
                    ref={tsInputRef}
                    type="text"
                    value={tsInputVal}
                    onChange={(e) => setTsInputVal(e.target.value)}
                    placeholder="e.g. 08:32"
                    className="w-24 px-2.5 py-1 text-xs font-jetbrains font-bold rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-black/60 text-slate-900 dark:text-white text-center focus:outline-none focus:border-amber-500 shadow-xs"
                  />
                  {tsInputVal && (
                    <button
                      type="button"
                      onClick={() => setTsInputVal("")}
                      className="ml-1 text-slate-400 hover:text-slate-700 dark:hover:text-white text-xs px-1"
                      title="Clear timestamp"
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Quick Step Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => adjustTs(-30)}
                    className="text-[11px] font-jetbrains px-2 py-1 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 transition-colors"
                    title="Rewind 30 seconds"
                  >
                    -30s
                  </button>
                  <button
                    type="button"
                    onClick={() => adjustTs(30)}
                    className="text-[11px] font-jetbrains px-2 py-1 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 transition-colors"
                    title="Forward 30 seconds"
                  >
                    +30s
                  </button>
                </div>
              </div>

              {/* Auto Detect Button */}
              <button
                type="button"
                onClick={captureTimestamp}
                className="flex items-center gap-1.5 text-xs font-jetbrains font-bold px-3 py-1 rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-[#FFCC00] transition-all shadow-xs"
                title="Detect playback time from video player"
              >
                <Clock className="w-3.5 h-3.5" />
                Auto-Detect
              </button>
            </div>

            {/* Question Textarea */}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What's your question about this lesson? e.g. Why did the price bounce at the demand zone here?"
              rows={3}
              className="w-full bg-white dark:bg-[#050505] border border-slate-300 dark:border-white/[0.06] rounded-xl px-4 py-3 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#52525B] resize-none focus:outline-none focus:border-[#A855F7] transition-colors shadow-xs"
            />

            <Button
              type="submit"
              disabled={!text.trim() || submitting}
              className="w-full bg-[#A855F7] hover:bg-[#9333ea] !text-white font-bold h-10 rounded-xl shadow-xs"
            >
              <Send className="w-3.5 h-3.5 mr-2" />
              {submitting ? "Submitting..." : "Submit Doubt to Mentor"}
            </Button>
          </form>

          {/* My doubts for this lesson */}
          {doubts.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-white/[0.06]">
              <p className="text-[10px] font-jetbrains text-slate-500 dark:text-[#52525B] uppercase tracking-widest font-semibold">
                My Doubts for this Lesson ({doubts.length})
              </p>
              {loadingDoubts ? (
                <div className="py-4 flex justify-center">
                  <div className="w-5 h-5 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                doubts.map((d) => (
                  <div
                    key={d.doubt_id}
                    className="bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/[0.04] rounded-xl p-3.5 cursor-pointer hover:bg-slate-100 dark:hover:bg-white/[0.04] transition-all"
                    onClick={() => setSelectedDoubtId(d.doubt_id)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-sm font-outfit text-slate-900 dark:text-white line-clamp-2 flex-1 font-medium">
                        {d.doubt_text}
                      </p>
                      <Badge className={`border-0 text-[10px] font-jetbrains shrink-0 ${STATUS_COLORS[d.status] || "bg-slate-100 dark:bg-white/5 text-slate-600"}`}>
                        {(d.status || "pending").replace("_", " ").toUpperCase()}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-3 text-xs font-jetbrains">
                      {d.timestamp_in_video > 0 && (
                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-bold bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 rounded-md border border-amber-200 dark:border-amber-500/20">
                          <Clock className="w-3 h-3" /> {fmtTs(d.timestamp_in_video)}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-[#00d991] font-semibold ml-auto">
                        <Eye className="w-3.5 h-3.5" /> View Discussion
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
